import React from 'react';
import clsx from 'clsx';
import { useUtils } from '~/shared/hooks/useUtils';
import { useTranslation } from '~/shared/utils/translation';
import { PriceLike } from '~/lib/salesforce/types';
import { PriceTypes } from '~/shared/components/ProductCard/components/Price';
import styles from './ProductPrice.module.scss';
import { PromotionType } from '~/lib/data-contract/salesforce';
import { shouldHighlightDefaultPromoPrice } from '~/shared/utils/shouldHighlightDefaultPromoPrice';

export const ProductPrice = ({
    prices,
    defaultLabel,
    isGiftCard = false,
    minimumPrice,
    promoType,
    moduleType,
}: {
    defaultLabel?: string;
    prices?: PriceLike[];
    isGiftCard?: boolean;
    minimumPrice?: number;
    promoType?: string;
    moduleType?: 'smallProduct' | 'reservationProduct';
}) => {
    const { convertPriceToLocale } = useUtils();
    const { translate } = useTranslation();

    if (!prices?.length) return null;

    const hasMemberPrice = prices.some((price) => price.promotype === PromotionType.MEMBER_PRICE);

    return (
        <div
            className={clsx(styles.priceWrap, {
                [styles.smallProduct]: moduleType === 'smallProduct',
                [styles.reservationProduct]: moduleType === 'reservationProduct',
            })}
        >
            {isGiftCard ? (
                <div className={styles.priceContainer}>
                    <div
                        aria-label={translate('common.from')}
                        className={clsx(styles.priceLabel)}
                    >{`${translate('common.from')}`}</div>
                    <div
                        aria-label={convertPriceToLocale(minimumPrice)}
                        className={clsx(styles.price)}
                    >
                        {` ${convertPriceToLocale(minimumPrice)} `}
                    </div>
                </div>
            ) : (
                prices.map(({ value, type = PriceTypes.DEFAULT, label, promotype }, index) => {
                    const shouldHighlightDefaultPrice = shouldHighlightDefaultPromoPrice({
                        priceType: type,
                        promoType,
                        prices,
                    });

                    const displayType = shouldHighlightDefaultPrice ? PriceTypes.SALE : type;
                    const displayLabel =
                        hasMemberPrice && prices.length > 1
                            ? promotype === PromotionType.MEMBER_PRICE
                                ? translate('pdp.memberPrice')
                                : translate('pdp.notMemberPrice')
                            : label ?? defaultLabel;

                    return (
                        <div className={styles.priceContainer} key={`${String(value)}-${index}`}>
                            <div
                                aria-label={displayLabel}
                                className={clsx(
                                    styles.priceLabel,
                                    type && styles[displayType.toLowerCase()],
                                )}
                            >
                                {displayLabel}
                            </div>
                            <div
                                aria-label={convertPriceToLocale(value)}
                                className={clsx(
                                    styles.price,
                                    styles[`price--${displayType.toLowerCase()}`],
                                )}
                            >
                                {convertPriceToLocale(value)}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
};
